import { useMutation, useQuery } from 'react-query';
import {
  createFunction,
  getFunction,
  listFunctions,
  removeFunction,
  updateFunction,
} from './Functions.service';
import notificationError from '@/utils/functions/notificationError';
import {
  EmployeeFunction,
  EmployeeFunctionRequestParams,
} from './Functions.types';
import { notification } from 'antd';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';
import { queryClient } from '@/Root';

export function useFunctions(
  params?: EmployeeFunctionRequestParams,
  enabled = true,
) {
  return useQuery(['functions', params], () => listFunctions(params), {
    onError(error) {
      notificationError(error);
    },
    enabled,
  });
}

export function useFunction(id?: number) {
  return useQuery(
    ['function/detail', id],
    () => (id ? getFunction(id) : undefined),
    {
      enabled: !!id,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useFunctionMutation() {
  return useMutation(
    async (id: number) => {
      const cache = queryClient.getQueryData<EmployeeFunction>([
        'function/detail',
        id,
      ]);

      if (!!cache) return cache;

      return await getFunction(id);
    },
    {
      onSuccess(data) {
        queryClient.setQueryData(['function/detail', data.id], data);
      },
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useFunctionCreate() {
  const queryChanel = useQueryChanel();

  return useMutation(createFunction, {
    onSuccess() {
      notification.success({
        message: 'Sucesso!',
        description: 'Cargo criado com sucesso!',
      });

      queryChanel({ invalidateQueries: [['functions']] });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useFunctionUpdate() {
  const queryChanel = useQueryChanel();

  return useMutation(updateFunction, {
    onSuccess(_, variables) {
      notification.success({
        message: 'Sucesso!',
        description: 'Cargo editado com sucesso!',
      });

      queryChanel({
        removeQueries: [['functions'], ['function/detail', variables.id]],
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useFunctionRemove() {
  const queryChanel = useQueryChanel();

  return useMutation(removeFunction, {
    onSuccess() {
      notification.success({
        message: 'Sucesso!',
        description: 'Cargo removido com sucesso!',
      });

      queryChanel({ invalidateQueries: [['functions']] });
    },
    onError(error) {
      notificationError(error);
    },
  });
}
